import React, { useCallback, useEffect, useRef, useState } from "react";
import { Spinner } from "@fluentui/react-components";
import { useNavigate } from "react-router";
import { telephonyService, type CallLogDto } from "../../services/telephonyService";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import { openComposeWindow } from "../mail/ComposeMailWindow";
import { openVorgangFromCall } from "./callVorgangFlow";
import { createRueckrufVorgangFromCall } from "./callRueckrufVorgang";
import AssignNumberDialog from "./AssignNumberDialog";
import CallVorgangButton from "./CallVorgangButton";
import { anzahlZuordnungen } from "./callZuordnungen";
import CallVorgangZuweisenDialog from "./CallVorgangZuweisenDialog";
import CallKontakteBlock from "./CallKontakteBlock";
import CallVorgaengeBlock from "./CallVorgaengeBlock";
import { callFollowUpTemplate } from "./callFollowUpMail";
import { useAuth } from "../../context/AuthContext";
import { I } from "../redesign/kvpIcons";
import {
    ArrowInIcon,
    ArrowOutIcon,
    PersonAddIcon,
    PersonIcon,
    PlayIcon,
    DIRECTION_LABEL,
    isMissedInbound,
    fmtDate,
    fmtTime,
    fmtDuration,
    fmtDateTime,
    anzeigeInitialen,
    counterpartyNumber,
    displayNameOf,
    statusLabel,
    callPath,
    hatWeg,
} from "./telephonyUtils";
import "../redesign/contactRedesign.css";

/**
 * CallDetailPanel — das Anruf-Detail (Mockup #10), SP4.
 *
 * Master-Detail rechts auf der Telefon-Seite: aus der Liste ausgewählter Anruf →
 * diese Karte ersetzt die Liste (Zurück kehrt zurück). Im Refined/kvp-rd-Stil.
 *
 * Inhalt:
 *  - Kopf: Richtungs-Icon, Name/Nummer, Zurück.
 *  - Metadaten-Grid: Richtung, Datum, Uhrzeit, Dauer, Kunde, **Angenommen von**
 *    (kein „Thema" — bewusst weggelassen, s. Vorplanung Entscheidung #5).
 *  - Aktionen: Zurückrufen (SDK via `callNumber`), E-Mail (Compose-Fenster),
 *    Notiz (springt zum Notiz-Feld), Erledigt-Toggle (PATCH …/:id mit {resolved}).
 *  - Recording-Player (falls Aufzeichnung vorhanden).
 *  - Transkript-Abschnitt: Platzhalter „bald" mit KI-Badge + angedeutetem
 *    Chat-Layout — NICHT ans Backend verdrahtet (Entscheidung #12).
 */

const SAVE_DEBOUNCE_MS = 700;

// ── kleine Inline-Icons (kvpIcons-Stil) nur fürs Detail ──────────────────────
const ico = (d: React.ReactNode, size = 16): React.ReactElement => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        {d}
    </svg>
);
const SparkleIcon = ico(
    <>
        <path d="M10 3.5l1.4 3.6L15 8.5l-3.6 1.4L10 13.5 8.6 9.9 5 8.5l3.6-1.4z" />
        <path d="M15.5 13.5l.6 1.6 1.6.6-1.6.6-.6 1.6-.6-1.6-1.6-.6 1.6-.6z" />
    </>,
);
const ChatIcon = ico(
    <>
        <path d="M3.5 5.5a2 2 0 012-2h9a2 2 0 012 2v6a2 2 0 01-2 2H8l-3.5 3v-3a1 1 0 01-1-1z" />
        <path d="M7 7.5h6M7 10h4" />
    </>,
);

export interface CallDetailPanelProps {
    call: CallLogDto;
    onBack: () => void;
    /** Wählt die Gegenstelle erneut (App-weiter SDK-Helper). */
    onCallback: (c: CallLogDto) => void;
    /** Meldet einen geänderten Anruf zurück (Notiz/Erledigt) für die Liste. */
    onChanged: (next: CallLogDto) => void;
    /**
     * Die Nummer wurde einem Kontakt zugeordnet. Das Backend zieht dabei auch
     * die übrigen Anrufe derselben Nummer nach — die Liste sollte sich deshalb
     * neu vom Server holen, nicht nur diese eine Zeile ersetzen.
     */
    onKontaktZugeordnet?: () => void;
}

const CallDetailPanel: React.FC<CallDetailPanelProps> = ({
    call,
    onBack,
    onCallback,
    onChanged,
    onKontaktZugeordnet,
}) => {
    const { openWindow } = useWindowManager();
    const navigate = useNavigate();
    const { user } = useAuth();
    // SP8: „ich" für den Auto-Rückruf-Vorgang (hauptverantwortlich = [ich]).
    const currentUserId: string | null = user?.id ?? null;
    const noteRef = useRef<HTMLTextAreaElement | null>(null);

    const [notes, setNotes] = useState(call.notes ?? "");
    const [savingNotes, setSavingNotes] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const [resolving, setResolving] = useState(false);
    const [resolveError, setResolveError] = useState<string | null>(null);

    // Letzter getippter Wert + zuletzt persistierter Wert + offener Debounce-Timer
    // in Refs, damit ein ausstehender Save beim Verlassen NICHT verlorengeht
    // (Unmount via „Zurück"/Anrufwechsel cleart sonst den Timeout still).
    const notesRef = useRef(notes);
    notesRef.current = notes;
    const savedNotesRef = useRef(call.notes ?? "");
    const timeoutRef = useRef<number | null>(null);

    // Persistiert die Notiz via PATCH /telephony/calls/:id. `silent` für den
    // Unmount-Flush (kein setState mehr auf der demontierten Komponente).
    const flushNotes = useCallback(
        async (silent: boolean) => {
            if (timeoutRef.current != null) {
                window.clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            const value = notesRef.current;
            if (value === savedNotesRef.current) return;
            if (!silent) {
                setSavingNotes(true);
                setSaveError(null);
            }
            try {
                const updated = await telephonyService.updateCall(call.id, { notes: value });
                savedNotesRef.current = value;
                onChanged(updated);
            } catch (e) {
                if (!silent) setSaveError((e as Error).message);
            } finally {
                if (!silent) setSavingNotes(false);
            }
        },
        [call.id, onChanged],
    );

    // Debounce-Save der Notiz.
    useEffect(() => {
        if (notes === savedNotesRef.current) return;
        timeoutRef.current = window.setTimeout(() => {
            void flushNotes(false);
        }, SAVE_DEBOUNCE_MS);
        return () => {
            if (timeoutRef.current != null) {
                window.clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [notes, flushNotes]);

    // Best-effort-Flush genau beim Unmount (Zurück zur Liste / Anrufwechsel):
    // über ein Ref auf die aktuelle flushNotes, damit der Cleanup NICHT bei jeder
    // onChanged-Identität, sondern nur beim echten Demontieren feuert.
    // Die Notiz aus dem Gespräch wird erst nach dem Auflegen angehängt — oft
    // nachdem die Zeile schon geladen war (08.10.2026: „Notiz wird nicht
    // angezeigt"). Kommt ein neuer Stand von außen und hier ist nichts
    // ungespeichert getippt, gilt er. Sonst würde das nächste Tippen die
    // angehängte Notiz mit dem alten Stand überschreiben.
    const aussenNotiz = call.notes ?? "";
    useEffect(() => {
        if (aussenNotiz === savedNotesRef.current) return;
        if (notesRef.current !== savedNotesRef.current) return;
        savedNotesRef.current = aussenNotiz;
        setNotes(aussenNotiz);
    }, [aussenNotiz]);

    const flushRef = useRef(flushNotes);
    flushRef.current = flushNotes;
    useEffect(() => {
        return () => {
            void flushRef.current(true);
        };
    }, []);

    const isResolved = !!call.resolvedAt;

    // Erledigt-Toggle: {resolved:true} stempelt resolvedAt + User serverseitig,
    // {resolved:false} öffnet wieder. Antwort enthält den aufgelösten Namen.
    const onToggleResolved = useCallback(async () => {
        setResolving(true);
        setResolveError(null);
        try {
            const updated = await telephonyService.updateCall(call.id, { resolved: !isResolved });
            onChanged(updated);
        } catch (e) {
            setResolveError((e as Error).message);
        } finally {
            setResolving(false);
        }
    }, [call.id, isResolved, onChanged]);

    const focusNote = useCallback(() => {
        const el = noteRef.current;
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus();
    }, []);

    // A9/SP4: Mail-/Vorgang-Aktion auf einem Anruf OHNE Kontakt öffnet zuerst den
    // Nummer-Zuweisen-Dialog; danach läuft die Aktion mit dem Kontakt weiter.
    const [assignPurpose, setAssignPurpose] = useState<"mail" | "vorgang" | "kontakt" | null>(null);
    // Menüpunkt „Bestehendem Vorgang zuweisen": braucht KEINEN Kontakt (der
    // gewählte Vorgang hat seinen Kunden bereits) und geht deshalb direkt auf.
    const [vorgangZuweisen, setVorgangZuweisen] = useState(false);

    // Eigentliche Aktionen (sobald ein Kontakt vorliegt).
    // E-Mail (SP5/A10): Compose vorbefüllt mit der Follow-up-Vorlage nach
    // Richtung (eingehend verpasst → (b), sonst nicht erreicht → (a)) und der
    // Standard-E-Mail des Kontakts als Empfänger. Ohne Kontakt-Mail bleibt das
    // Empfänger-Feld leer (Hinweis: Adresse muss ergänzt werden). Der Versand
    // passiert bewusst im Compose-Fenster (kein Auto-Send).
    const runEmail = useCallback(
        (c: CallLogDto) => {
            const tpl = callFollowUpTemplate(c.direction);
            openComposeWindow(openWindow, {
                to: c.contactEmail ? [c.contactEmail] : [],
                subject: tpl.subject,
                // T-41: Die Büroklammer startet mit den Dokumenten des Anrufkontakts.
                contactId: c.contactId ?? null,
                // A2: Firma → „Hallo zusammen,"; Person → nur mit sicherem Vornamen,
                // sonst keine automatische Anrede (kein Gruß aus dem Anzeigenamen).
                body: tpl.body({ isCompany: c.contactIsCompany ?? undefined, firstName: c.contactFirstName }),
                // SP8 (A11/A12): Nach ERFOLGREICHEM Versand automatisch einen
                // „Rückruf <Kontakt>"-Vorgang anlegen und den Anruf verknüpfen +
                // erledigen. Nur bei bekanntem Kontakt; `onChanged` zeigt das
                // Erledigt-Banner im Detail sofort. Kontaktlose Anrufe laufen
                // zuvor durch den Zuweisen-Dialog (SP4).
                onSent: c.contactId
                    ? async () => {
                          const updated = await createRueckrufVorgangFromCall(c, currentUserId);
                          onChanged(updated);
                      }
                    : undefined,
            });
        },
        [openWindow, currentUserId, onChanged],
    );
    // Vorgang (SP7/A13): zweistufiger Erstell-Flow mit Kontakt-Vorbelegung,
    // Betreff „Anruf vom <Datum>" + Erst-Notiz. Nach dem Anlegen wird der Anruf
    // an den Vorgang gebunden und automatisch erledigt (Entscheidung 59);
    // `onChanged` aktualisiert das Detail (Erledigt-Banner erscheint sofort).
    const runVorgang = useCallback(
        (c: CallLogDto) => {
            openVorgangFromCall(openWindow, c, onChanged);
        },
        [openWindow, onChanged],
    );

    const onEmail = useCallback(() => {
        if (call.contactId) runEmail(call);
        else setAssignPurpose("mail");
    }, [call, runEmail]);
    const onNewVorgang = useCallback(() => {
        if (call.contactId) runVorgang(call);
        else setAssignPurpose("vorgang");
    }, [call, runVorgang]);
    const onVorgangZuweisen = useCallback(() => setVorgangZuweisen(true), []);

    // Nach der Zuordnung: Liste aktualisieren + Aktion fortsetzen.
    const onNumberAssigned = useCallback(
        (updated: CallLogDto | null) => {
            // Von hier geht der Dialog immer MIT Anruf auf; `null` kommt nur aus
            // dem Nach-dem-Gespräch-Weg, den es in diesem Panel nicht gibt.
            if (!updated) return;
            onChanged(updated);
            onKontaktZugeordnet?.();
            const purpose = assignPurpose;
            setAssignPurpose(null);
            if (purpose === "mail") runEmail(updated);
            else if (purpose === "vorgang") runVorgang(updated);
        },
        [assignPurpose, onChanged, onKontaktZugeordnet, runEmail, runVorgang],
    );

    // „Kontakt": mit Kontakt → die Akte öffnen; ohne → zuordnen oder anlegen.
    const onKontakt = useCallback(() => {
        if (call.contactId) navigate(`/contacts/${call.contactId}`);
        else setAssignPurpose("kontakt");
    }, [call.contactId, navigate]);

    const missed = isMissedInbound(call);
    const name = displayNameOf(call);
    const num = counterpartyNumber(call);
    const initials = anzeigeInitialen(call);
    // T-12: Firma, für die eine Person anruft (bei einer Firma als Kontakt keine).
    const firma = call.contactIsCompany ? null : (call.contactCompanyName ?? "").trim() || null;
    // A6: erledigte verpasste Anrufe neutral/schwarz statt rot (auch im Detail).
    const dirClass = missed
        ? isResolved
            ? "missed resolved"
            : "missed"
        : call.direction === "outbound"
          ? "out"
          : "in";
    const dirIcon = call.direction === "outbound" ? ArrowOutIcon : ArrowInIcon;

    return (
        <div className="tp-detail">
            <div className="tp-detail-head">
                <button type="button" className="tp-back" onClick={onBack}>
                    {I.back}
                    Zurück zur Liste
                </button>
                <div className="tp-detail-id">
                    <span className={`tp-dir ${dirClass}`} aria-hidden>
                        {dirIcon}
                    </span>
                    <span className="tp-avatar" aria-hidden>
                        {initials ?? I.phone}
                    </span>
                    <div>
                        <div className="tp-detail-name">{name}</div>
                        {/* Nummer nur zeigen, wenn ein echter Kontaktname vorliegt — sonst
                            IST `name` bereits die Nummer (displayNameOf-Fallback) und die
                            numline wäre eine Dopplung (analog CallRow). */}
                        {call.contactDisplayName && (firma || num) && (
                            <div className="tp-detail-numline">
                                {[firma, num].filter(Boolean).join(" · ")}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="tp-detail-body">
                {/* Erledigt-Status-Banner */}
                {isResolved && (
                    <div className="tp-resolved-banner">
                        {I.check}
                        <span>
                            Erledigt
                            {call.resolvedAt ? ` am ${fmtDateTime(call.resolvedAt)}` : ""}
                            {call.resolvedByUserName ? ` von ${call.resolvedByUserName}` : ""}
                        </span>
                    </div>
                )}

                <div className="tp-meta-grid">
                    <Meta
                        k="Richtung"
                        v={`${DIRECTION_LABEL[call.direction]}${missed ? " · Verpasst" : ""}`}
                    />
                    <Meta k="Datum" v={fmtDate(call.startedAt)} />
                    <Meta k="Uhrzeit" v={fmtTime(call.startedAt)} />
                    <Meta k="Dauer" v={fmtDuration(call.talkDurationSec)} />
                    <Meta k="Kunde" v={call.contactDisplayName ?? "—"} />
                    {firma && (
                        <Meta
                            k="Firma"
                            v={
                                call.contactCompanyId ? (
                                    <button
                                        type="button"
                                        className="tp-meta-link"
                                        onClick={() => navigate(`/contacts/${call.contactCompanyId}`)}
                                        title="Firmenakte öffnen"
                                    >
                                        {firma}
                                    </button>
                                ) : (
                                    firma
                                )
                            }
                        />
                    )}
                    <Meta
                        k={call.direction === "outbound" ? "Geführt von" : "Angenommen von"}
                        v={call.userName ?? "—"}
                    />
                    {call.missedGroupName && (
                        <Meta k="Team" v={call.missedGroupName} />
                    )}
                    {call.status && <Meta k="Status" v={statusLabel(call.status)} />}
                </div>

                {/* Der Weg des Anrufs — nur, wenn er mehr als eine Station hatte.
                    Ohne ihn sieht ein Mitarbeiter bei einer Weiterleitung bloß
                    den Namen eines Kollegen und weiß nicht, warum der Anruf bei
                    ihm auftaucht. */}
                {hatWeg(call) && (
                    <div className="tp-weg">
                        <div className="tp-weg-k">Weg des Anrufs</div>
                        <ol className="tp-weg-liste">
                            {callPath(call).map((schritt, i) => (
                                <li
                                    key={`${schritt.label}-${i}`}
                                    className={`tp-weg-schritt${schritt.answered ? " ok" : ""}`}
                                >
                                    <span className="tp-weg-ziel">{schritt.label}</span>
                                    <span className="tp-weg-ergebnis">{schritt.outcome}</span>
                                </li>
                            ))}
                        </ol>
                    </div>
                )}

                {/* Mehrere Kontakte je Anruf: Hauptkontakt + weitere, hinzufügen/
                    entfernen/zum Hauptkontakt machen. */}
                <CallKontakteBlock call={call} onChanged={onChanged} />

                {/* Vorgänge je Anruf (PO 01.10.2026): Chips, × löst eine Zuordnung. */}
                <CallVorgaengeBlock call={call} onChanged={onChanged} />

                {/* Aktions-Reihenfolge (A7): Zurückrufen → Erledigt → E-Mail →
                    Vorgang → Notiz. „Vorgang" öffnet ein Menü: neu anlegen oder
                    an einen bestehenden hängen. */}
                <div className="tp-detail-actions">
                    <button
                        type="button"
                        className="tp-detail-btn primary"
                        disabled={!num}
                        onClick={() => onCallback(call)}
                        title={num ? `Zurückrufen: ${num}` : "Keine Nummer"}
                    >
                        {I.phone}
                        Zurückrufen
                    </button>
                    <button
                        type="button"
                        className={`tp-detail-btn${isResolved ? " ok" : ""}`}
                        onClick={onToggleResolved}
                        disabled={resolving}
                        aria-pressed={isResolved}
                    >
                        {resolving ? <Spinner size="tiny" /> : I.check}
                        {isResolved ? "Erledigt" : "Als erledigt markieren"}
                    </button>
                    <button type="button" className="tp-detail-btn" onClick={onEmail}>
                        {I.mail}
                        E-Mail
                    </button>
                    <CallVorgangButton
                        variante="detail"
                        onNeu={onNewVorgang}
                        onZuweisen={onVorgangZuweisen}
                        anzahl={anzahlZuordnungen(call)}
                    />
                    <button
                        type="button"
                        className="tp-detail-btn"
                        onClick={onKontakt}
                        title={
                            call.contactId
                                ? `Kontakt öffnen: ${call.contactDisplayName ?? ""}`.trim()
                                : "Nummer einem Kontakt zuordnen oder neuen Kontakt anlegen"
                        }
                    >
                        {call.contactId ? PersonIcon : PersonAddIcon}
                        {call.contactId ? "Kontakt öffnen" : "Kontakt zuordnen"}
                    </button>
                    <button type="button" className="tp-detail-btn" onClick={focusNote}>
                        {I.edit}
                        Notiz
                    </button>
                </div>
                {resolveError && (
                    <span className="tp-muted" style={{ color: "var(--danger)" }}>
                        {resolveError}
                    </span>
                )}

                {call.recordingFile && (
                    <div className="tp-block">
                        <div className="tp-block-h">
                            {PlayIcon}
                            Aufzeichnung
                        </div>
                        <RecordingPlayer callId={call.id} />
                    </div>
                )}

                <div className="tp-block">
                    <div className="tp-block-h">
                        {I.edit}
                        Notiz
                        {savingNotes && <span className="tp-muted">speichert …</span>}
                        {saveError && (
                            <span className="tp-muted" style={{ color: "var(--danger)" }}>
                                {saveError}
                            </span>
                        )}
                    </div>
                    <textarea
                        ref={noteRef}
                        className="tp-note"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        onBlur={() => void flushNotes(false)}
                        placeholder="Notiz zum Anruf …"
                    />
                </div>

                {/* Transkript: Platzhalter „bald" — bewusst NICHT ans Backend
                    verdrahtet (Vorplanung Entscheidung #12). */}
                <div className="tp-block">
                    <div className="tp-block-h">
                        {ChatIcon}
                        Transkript
                        <span className="tp-ki-badge">
                            {SparkleIcon}
                            KI-generiert
                        </span>
                    </div>
                    <div className="tp-transcript-ph">
                        <div className="tp-transcript-chat" aria-hidden>
                            <div className="tp-chat-row left">
                                <span className="tp-chat-bubble" />
                            </div>
                            <div className="tp-chat-row right">
                                <span className="tp-chat-bubble sm" />
                            </div>
                            <div className="tp-chat-row left">
                                <span className="tp-chat-bubble lg" />
                            </div>
                            <div className="tp-chat-row right">
                                <span className="tp-chat-bubble" />
                            </div>
                        </div>
                        <div className="tp-transcript-soon">
                            {SparkleIcon}
                            <span>Das KI-Transkript des Gesprächs erscheint hier bald.</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* A9/SP4: Nummer-Zuweisen-Dialog für Anrufe ohne Kontakt. */}
            {assignPurpose && (
                <AssignNumberDialog
                    call={call}
                    purpose={assignPurpose}
                    onClose={() => setAssignPurpose(null)}
                    onAssigned={onNumberAssigned}
                />
            )}

            {/* Menüpunkt „Bestehendem Vorgang zuweisen". */}
            {vorgangZuweisen && (
                <CallVorgangZuweisenDialog
                    call={call}
                    onClose={() => setVorgangZuweisen(false)}
                    onAssigned={onChanged}
                />
            )}
        </div>
    );
};

// ── Metadaten-Zelle ──────────────────────────────────────────────────────────
const Meta: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
    <div className="tp-meta">
        <span className="tp-meta-k">{k}</span>
        <span className="tp-meta-v">{v}</span>
    </div>
);

// ── Recording-Player (Blob → Object-URL; Bearer-Header übers patched fetch) ───
const RecordingPlayer: React.FC<{ callId: number }> = ({ callId }) => {
    const [url, setUrl] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        return () => {
            if (url) URL.revokeObjectURL(url);
        };
    }, [url]);

    const onPlay = useCallback(async () => {
        if (url) return;
        setBusy(true);
        setErr(null);
        try {
            const blob = await telephonyService.fetchCallRecordingAudio(callId);
            if (!blob) {
                setErr("Keine Aufzeichnung verfügbar.");
                return;
            }
            setUrl(URL.createObjectURL(blob));
        } catch (e) {
            setErr((e as Error).message);
        } finally {
            setBusy(false);
        }
    }, [url, callId]);

    if (url) {
        // eslint-disable-next-line jsx-a11y/media-has-caption
        return <audio className="tp-audio" controls src={url} />;
    }
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button type="button" className="tp-detail-btn" onClick={onPlay} disabled={busy}>
                {busy ? <Spinner size="tiny" /> : PlayIcon}
                Aufzeichnung anhören
            </button>
            {err && (
                <span className="tp-muted" style={{ color: "var(--danger)" }}>
                    {err}
                </span>
            )}
        </div>
    );
};

export default CallDetailPanel;
